import { Button, Card, Checkbox, Datepicker, Label, Radio, Select, TextInput } from "flowbite-react";
import { HiCheck, HiTrash } from 'react-icons/hi'
import { useReducerAsState } from "../hooks/useReducerAsState";
import useCategories from "../hooks/useCategories";
import { useEffect } from "react";
import useNotes from "../hooks/useNotes";
import { Note } from "../types/notes";
import useDebounce from "../hooks/useDebounce";

interface HeaderSettingsType {
    type: 'category-note' | 'specific-note';
    category_id: number;
    note_id: number;
    published_until: number | null;
}

interface State {
    type: 'category-note' | 'specific-note';
    category_id: number;
    note_id: number;
    published_until: number | null;
    noteTitle: string;
    chosenNote: Note | null;
    hasDate: number;
    id: string;
}

interface Props {
    onChange: (data:any, position:number) => void;
    position: number;
    data?: HeaderSettingsType;
    shouldClear: boolean;
}


const HeaderNote = (props: Props) => {
    const { categories, fetchCategories } = useCategories();
    const { findNote, fetchNotes, notes } = useNotes();

    const [state, setState] = useReducerAsState<State>({
        type: 'category-note',
        category_id:  0,
        note_id:  0,
        published_until:  null,
        noteTitle: "",
        chosenNote: null,
        hasDate: 0,
        id: Math.random().toString(36).substring(2, 15),
    });

    const debounceSearchTerm = useDebounce(state.noteTitle, 500)

    const handleOptionChange = (e:any) => {
        const value = e.target.value;
        
        if(value === "category-note"){
            setState({ type: value, hasDate: 0, published_until: null, chosenNote: null, note_id: 0, noteTitle: "" })
            return
        }
        setState({ type: value })
    }

    const handleCategoryChange = (e:any) => {
        setState({ category_id: parseInt(e.target.value) })
    } 

    const handleNoteChange = async (e: any) => {
        if(!e) return
        setState({ noteTitle: e.target.value})
    }

    const handleNoteSelect = async (note: Note) => {
        setState({ chosenNote: note, category_id:0, note_id: note.id });
    }

    const removeChosenNote = () => {
        setState({ chosenNote: null, category_id:0, note_id: 0 });
    }

    const toggleDate = (e:any) => {
        setState({ hasDate: e.target.checked ? 1 : 0, published_until: e.target.checked ? new Date().getTime() : null })
    }

    const handleDateChange = (e: any) => {
        setState({ published_until: new Date(e).getTime() })
    }

    const initialize = async () => {
        await fetchCategories()
        if(state.type === 'specific-note'){
            await findNote(state.note_id)
        }
    }

    const handleFindNotes = async () => {
        if(debounceSearchTerm.length === 0) return

        setState({ chosenNote: null })
        await fetchNotes({
            page:1,
            limit: 3,
            keywords: debounceSearchTerm
        })
    }

    useEffect(() => {
        initialize();
        // eslint-disable-next-line
    },[]);

    const initializeData = async () => {
        if(!props.data) return;

        // has note id
        let chosenNote = null;
        if(props.data.note_id){
           chosenNote = await findNote(props.data.note_id)
        }

        setState({
            ...props.data,
            chosenNote,
            hasDate: props.data.published_until !== null ? 1 : 0,
        });
    }

    useEffect(() => {
        initializeData()
        // eslint-disable-next-line
    },[props.data])

    useEffect(() => {
        handleFindNotes()
        // eslint-disable-next-line
    },[debounceSearchTerm])

    useEffect(() => {
        props.onChange(state, props.position)
     // eslint-disable-next-line
    }, [state]);

    useEffect(() => {
        if(props.shouldClear) {
            setState({ type: 'category-note', category_id: 0, note_id: 0, published_until: null, noteTitle: "", chosenNote: null, hasDate: 0 })
        }
            // eslint-disable-next-line
    },[props.shouldClear])

    return <form>
        <div className="flex flex-col mb-2">
            <fieldset className="flex max-w-md flex-col gap-4">
                <legend className="mb-4">Elija una opción</legend>
                <div className="flex items-center gap-2">
                    <Radio  id={`category-note-${state.id}`} name={`header-option-${state.id}`} value="category-note"  checked={state.type === "category-note"} onChange={handleOptionChange} />
                    <Label htmlFor={`category-note-${state.id}`}>Última nota de categoría</Label>
                </div>
                <div className="flex items-center gap-2">
                    <Radio  id={`specific-note-${state.id}`} name={`header-option-${state.id}`} value="specific-note"  checked={state.type === "specific-note"} onChange={handleOptionChange} />
                    <Label htmlFor={`specific-note-${state.id}`}>Nota específica</Label>
                </div>
            </fieldset>
        </div>
        {state.type === 'category-note' && <div className="flex flex-col mb-2">
                    <Label className="mb-2">Nota de:</Label>
                    <Select value={state.category_id} onChange={handleCategoryChange}>
                        <option value="0">- Elija una opción -</option>
                        <option value="-1">Todas las notas</option>
                        {categories && categories.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
                    </Select>
                </div>}
                {state.type === 'specific-note' && <div className="flex flex-col mb-2">
                    <Label className="mb-2">Nota de:</Label>
                    <TextInput 
                        placeholder="Escriba aquí"
                        onChange={handleNoteChange}
                        value={state.noteTitle}
                    />
                    <div className="flex flex-row mt-2 gap-4">
                        {state.chosenNote === null && notes && notes.map((item) => <Card 
                            key={item.id}
                            imgSrc={item.image ? item.image.url : ""}
                        >
                            <p>{item.title}</p>
                            <Button  onClick={() => handleNoteSelect(item)} color="gray">
                                <HiCheck />
                            </Button>
                        </Card>)}
                        {state.chosenNote !== null && <Card 
                            imgSrc={state.chosenNote.image ? `${process.env.REACT_APP_UPLOADS}/${state.chosenNote.image.id}` : ""}
                        >
                            <p>{state.chosenNote.title}</p>
                            <Button  onClick={removeChosenNote} color="failure">
                                <HiTrash />
                            </Button>
                        </Card>}
                    </div>
                </div>}
                {state.type === 'specific-note' && <div className="flex items-center gap-2 mb-2 mt-2">
                    <Checkbox checked={state.hasDate === 1} value={state.hasDate} id="programatic" onChange={toggleDate} />
                    <Label htmlFor="programatic">Habilitar publicación programática</Label>
                </div>}
                {state.hasDate === 1 && state.type === 'specific-note' && <div className="flex flex-col mb-2">
                    <Label className="mb-2">Mostrar hasta:</Label>
                    <Datepicker 
                        language="es-ES" 
                        labelTodayButton="Hoy" 
                        labelClearButton="Limpiar"
                        //defaultDate={state.hasDate === 1 && state.published_until}
                        //defaultValue={state.hasDate === 1 && state.published_until}
                        onSelectedDateChanged={handleDateChange}
                    />
                </div>}
    </form>
}

export default HeaderNote;